import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useGameStore from '../../stores/gameStore';
import useUIStore from '../../stores/uiStore';

interface UserInputFieldProps {
  variableName: string;
  placeholder?: string;
  maxLength?: number;
  autoSave?: boolean;
  debounce?: number;
  confirmationTarget?: string;
  className?: string;
  type?: string;
  multiline?: boolean;
  rows?: number;
}

const DEFAULT_DEBOUNCE_MS = 400;
const MIN_DEBOUNCE_MS = 150;
/** Allow long idle delays (e.g. live preview after typing pauses). */
const MAX_DEBOUNCE_MS = 10000;

/** Survives remounts when the scene re-interpolates around an auto-save refresh. */
const selectionByVariable = new Map<
  string,
  { start: number; end: number; value: string; wasFocused: boolean }
>();

const clampDebounce = (value: number | undefined) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_DEBOUNCE_MS;
  }
  return Math.min(MAX_DEBOUNCE_MS, Math.max(MIN_DEBOUNCE_MS, Math.floor(value)));
};

const UserInputField: React.FC<UserInputFieldProps> = ({
  variableName,
  placeholder,
  maxLength,
  autoSave = false,
  debounce,
  confirmationTarget,
  className,
  type = 'text',
  multiline = false,
  rows = 3
}) => {
  const storedValue = useGameStore(
    useCallback(
      (state) => state.userVariableValues[variableName]?.value ?? '',
      [variableName]
    )
  );
  const hasPendingInput = useGameStore(
    useCallback(
      (state) => state.pendingUserInputs[variableName] != null,
      [variableName]
    )
  );
  const currentSceneId = useGameStore((state) => state.currentScene?.id);
  const submitUserInput = useGameStore((state) => state.submitUserInput);
  const setPendingUserInput = useGameStore((state) => state.setPendingUserInput);
  const uiStore = useUIStore();

  const [inputValue, setInputValue] = useState<string>(storedValue ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;
  const focusedRef = useRef(false);
  const submittingRef = useRef(false);
  // Last value confirmed via submit (or scene sync). Pending drafts update `storedValue`
  // optimistically, so change detection must not use `storedValue` alone.
  const committedValueRef = useRef(storedValue ?? '');

  const autoSaveEnabled = autoSave === true;
  const effectiveDebounce = useMemo(
    () => (autoSaveEnabled ? clampDebounce(debounce) : undefined),
    [autoSaveEnabled, debounce]
  );

  const isInputFocused = useCallback(() => {
    const el = inputRef.current;
    return !!el && document.activeElement === el;
  }, []);

  const resolveInputElement = useCallback((): HTMLInputElement | HTMLTextAreaElement | null => {
    if (inputRef.current) {
      return inputRef.current;
    }
    return document.querySelector<HTMLInputElement | HTMLTextAreaElement>(
      `[data-testid="user-input-${variableName}"]`
    );
  }, [variableName]);

  const captureSelection = useCallback(
    (options?: { forceFocused?: boolean | undefined }) => {
      const el = inputRef.current;
      if (!el) {
        return;
      }
      const wasFocused =
        options?.forceFocused ?? (document.activeElement === el || focusedRef.current);
      selectionByVariable.set(variableName, {
        start: el.selectionStart ?? 0,
        end: el.selectionEnd ?? 0,
        value: el.value,
        wasFocused
      });
    },
    [variableName]
  );

  const restoreSelection = useCallback(() => {
    const saved = selectionByVariable.get(variableName);
    const el = resolveInputElement();
    if (!el || !saved) {
      return;
    }
    if (saved.value !== el.value) {
      return;
    }
    const max = el.value.length;
    const start = Math.min(saved.start, max);
    const end = Math.min(saved.end, max);
    if (saved.wasFocused && document.activeElement !== el) {
      el.focus({ preventScroll: true });
    }
    if (document.activeElement === el) {
      try {
        el.setSelectionRange(start, end);
      } catch {
        // Some input types (e.g. email) may not support setSelectionRange.
      }
    }
  }, [variableName, resolveInputElement]);

  // Restore caret after remount (scene re-interpolation) or value sync.
  useLayoutEffect(() => {
    restoreSelection();
  }, [inputValue, restoreSelection]);

  // Persist caret on unmount so a remounted field can restore it.
  useEffect(() => {
    return () => {
      const previous = selectionByVariable.get(variableName);
      captureSelection({
        forceFocused: submittingRef.current || previous?.wasFocused || focusedRef.current
      });
    };
  }, [captureSelection, variableName]);

  // When scene changes (navigation), always sync from store so we show the new scene's value
  useEffect(() => {
    const next = storedValue ?? '';
    committedValueRef.current = next;
    setInputValue(next);
    setDirty(false);
    setError(null);
    selectionByVariable.delete(variableName);
  }, [currentSceneId]); // eslint-disable-line react-hooks/exhaustive-deps -- only run when scene id changes

  // Keep local state in sync when store value changes from external updates.
  // Preserve in-progress edits (dirty) so a slower auto-save response cannot overwrite newer typing.
  // While focused, never rewrite the value (that resets the caret) — auto-save echoes must not
  // clobber the live field. Also preserve when the store is overwritten with empty AND we still
  // have uncommitted pending input (e.g. partner triggered same-scene refresh).
  useEffect(() => {
    if (storedValue !== inputValue) {
      if (dirty || isInputFocused()) {
        return;
      }
      const incomingEmpty = storedValue === '' || storedValue == null;
      const haveLocalDraft = inputValue !== '';
      if (haveLocalDraft && incomingEmpty && hasPendingInput) {
        return;
      }
      const next = storedValue ?? '';
      committedValueRef.current = next;
      setInputValue(next);
      setDirty(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedValue, hasPendingInput]);

  // Cleanup pending debounce timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  const runSubmission = useCallback(
    async (valueToSubmit: string, { silent }: { silent?: boolean } = {}) => {
      // Skip network/variable update when nothing actually changed since last commit.
      if (valueToSubmit === committedValueRef.current) {
        setDirty(false);
        return;
      }

      try {
        submittingRef.current = true;
        // Capture caret before scene refresh. Silent auto-save should keep focus in the field.
        // Never disable the input — that steals focus/caret.
        captureSelection({ forceFocused: silent ? true : undefined });
        if (!silent) {
          setIsSaving(true);
        }
        setError(null);
        const lengthOptions = maxLength !== undefined ? { maxLength } : undefined;
        const response = await submitUserInput(variableName, valueToSubmit, lengthOptions);
        committedValueRef.current =
          response && typeof response.value === 'string' ? response.value : valueToSubmit;
        // Only clear dirty if the field still shows what we submitted (user may have typed more).
        if (inputValueRef.current === valueToSubmit) {
          setDirty(false);
        }
        // Scene re-render may remount us; restore after commit.
        queueMicrotask(() => restoreSelection());
        requestAnimationFrame(() => restoreSelection());
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update value';
        setError(message);
        if (!silent) {
          uiStore.setError({
            code: 'USER_INPUT_ERROR',
            message
          });
        }
        throw err;
      } finally {
        submittingRef.current = false;
        if (!silent) {
          setIsSaving(false);
        }
      }
    },
    [submitUserInput, variableName, maxLength, uiStore, captureSelection, restoreSelection]
  );

  // Auto-save behaviour with debounce: submit after the user stops typing.
  useEffect(() => {
    if (!autoSaveEnabled || !dirty) {
      return;
    }

    if (inputValue === committedValueRef.current) {
      setDirty(false);
      return;
    }

    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const delay = effectiveDebounce ?? DEFAULT_DEBOUNCE_MS;
    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = null;
      void runSubmission(inputValue, { silent: true });
    }, delay);

    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [autoSaveEnabled, dirty, effectiveDebounce, inputValue, runSubmission]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setInputValue(value);
      setDirty(true);
      setError(null);
      // Keep caret snapshot current while typing (for remount restore).
      selectionByVariable.set(variableName, {
        start: event.target.selectionStart ?? value.length,
        end: event.target.selectionEnd ?? value.length,
        value,
        wasFocused: true
      });

      // Always keep pending in sync so option clicks can flush the latest draft immediately,
      // even when autoSave will also submit after an idle debounce.
      const lengthOptions = maxLength !== undefined ? { maxLength } : undefined;
      setPendingUserInput(variableName, value, lengthOptions);
    },
    [maxLength, setPendingUserInput, variableName]
  );

  const handleSelect = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = event.currentTarget;
      selectionByVariable.set(variableName, {
        start: target.selectionStart ?? 0,
        end: target.selectionEnd ?? 0,
        value: target.value,
        wasFocused: true
      });
    },
    [variableName]
  );

  const handleFocus = useCallback(() => {
    focusedRef.current = true;
    captureSelection({ forceFocused: true });
  }, [captureSelection]);

  const handleBlur = useCallback(() => {
    // Remount during in-flight auto-save can fire blur on the old node — ignore it.
    if (submittingRef.current) {
      captureSelection({ forceFocused: true });
      return;
    }
    focusedRef.current = false;
    captureSelection({ forceFocused: false });
    if (autoSaveEnabled && dirty && inputValue !== committedValueRef.current) {
      void runSubmission(inputValue);
    }
  }, [autoSaveEnabled, dirty, inputValue, runSubmission, captureSelection]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Multiline: allow Enter to insert newlines. Single-line autoSave: Enter submits.
      if (multiline) {
        return;
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (autoSaveEnabled && inputValue !== committedValueRef.current) {
          void runSubmission(inputValue);
        }
      }
    },
    [autoSaveEnabled, inputValue, multiline, runSubmission]
  );

  const rootClassName = useMemo(() => {
    const classes = ['rich-text-user-input'];
    if (className) {
      classes.push(className);
    }
    if (isSaving) {
      classes.push('rich-text-user-input--saving');
    }
    if (error) {
      classes.push('rich-text-user-input--error');
    }
    return classes.join(' ');
  }, [className, isSaving, error]);

  const inputClassName = `rich-text-user-input__input${multiline ? ' rich-text-user-input__input--multiline' : ''}`;

  return (
    <div
      className={rootClassName}
      data-variable={variableName}
      data-confirmation-target={confirmationTarget ?? undefined}
    >
      {multiline ? (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          className={inputClassName}
          value={inputValue}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={rows}
          onChange={handleChange}
          onSelect={handleSelect}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          aria-label={placeholder ?? variableName}
          aria-busy={isSaving || undefined}
          data-testid={`user-input-${variableName}`}
        />
      ) : (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={type}
          className={inputClassName}
          value={inputValue}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={handleChange}
          onSelect={handleSelect}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          aria-label={placeholder ?? variableName}
          aria-busy={isSaving || undefined}
          data-testid={`user-input-${variableName}`}
        />
      )}
      {isSaving && (
        <span
          className="rich-text-user-input__status"
          aria-live="polite"
          data-testid={`user-input-${variableName}-saving`}
        >
          Saving…
        </span>
      )}
      {error && (
        <span
          className="rich-text-user-input__error-message"
          role="status"
          aria-live="assertive"
          data-testid={`user-input-${variableName}-error`}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default UserInputField;
